import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

// A single-page app never reloads, so a screen-reader user is not told that
// the page changed. On every route change this:
// - gives the page its own <title> if the page does not set one (2.4.2)
// - moves focus to the new page's <h1>, or to <main>, so reading starts at
//   the top of the new page (2.4.3), and announces the title when there is
//   no heading to land on (4.1.3)
// Pages that set document.title themselves (Home, Portfolios, Pricing...)
// run their effect after this one, so their own title wins.

const SUFFIX = " | BossInvestor";
const TITLES = [
  [/^\/terms-and-conditions/i, "Terms & Conditions"],
  [/^\/privacy-policy/i, "Privacy Policy"],
  [/^\/escalation_matrix/i, "Escalation Matrix"],
  [/^\/about/i, "About us"],
  [/^\/contact/i, "Contact us"],
  [/^\/faq/i, "Frequently Asked Questions"],
  [/^\/shipping/i, "Shipping Policy"],
  [/^\/cancellationrefunds/i, "Cancellation & Refunds"],
  [/^\/investor_charter/i, "Investor Charter"],
  [/^\/grievance_redressal/i, "Grievance Redressal"],
  [/^\/statutory_details/i, "Statutory Details"],
  [/^\/investor_monthly_complaints/i, "Investor Monthly Complaints"],
  [/^\/accessibility/i, "Accessibility Statement"],
  [/^\/profile/i, "Your Profile"],
  [/^\/edit-profile/i, "Edit Profile"],
  [/^\/yourSubscriptions/i, "Your Subscriptions"],
  [/^\/checkout/i, "Checkout"],
  [/^\/kycVerification/i, "KYC Verification"],
  [/^\/esign-success/i, "Aadhaar eSign"],
  [/^\/session-timeout/i, "Session Expired"],
  [/^\/loginPaid/i, "Sign In"],
  [/^\/backtesterDetails/i, "Backtest Details"],
  [/^\/backtester\/results/i, "Backtest results"],
  [/^\/backtester\/tests/i, "My tests"],
  [/^\/backtester/i, "Backtester"],
  [/^\/StrategyBuilderPage/i, "Strategy Builder"],
  [/^\/factorSchool/i, "Factor School"],
  [/^\/factorInfo/i, "Factor School"],
  [/^\/marketPulse/i, "Market Pulse"],
  [/^\/tools/i, "Tools"],
  [/^\/glossary/i, "Glossary"],
  [/^\/bossai/i, "BossAI"],
];

const RouteAnnouncer = () => {
  const { pathname } = useLocation();
  const previous = useRef(pathname);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const match = TITLES.find(([re]) => re.test(pathname));
    if (match) document.title = match[1] + SUFFIX;

    // First load: the browser already announces the page, leave focus alone.
    if (previous.current === pathname) return undefined;
    previous.current = pathname;

    // Give the new page a moment to render its heading.
    const timer = setTimeout(() => {
      const heading = document.querySelector("main h1, [role='main'] h1, h1");
      const target =
        heading || document.querySelector("main, [role='main']");
      if (target) {
        if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
        target.focus({ preventScroll: true });
      }
      setMessage(heading ? "" : document.title);
    }, 300);
    return () => clearTimeout(timer);
  }, [pathname]);

  return (
    <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
      {message}
    </div>
  );
};

export default RouteAnnouncer;
